import {
  FC,
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
} from 'react';

import {
  DataPaginateResponse,
  PaginationRequestParams,
  ErrorResponse,
} from '~globals/types/api';
import { Nullable, SelectOption } from '~globals/types/commons';
import { RouteItemStateType } from '~globals/types/enums';
import {
  useParseFiltersRequestSearchItems,
  useDataTableRequest,
  useToggleDialogConfirmation,
} from '~hooks/index';
import { ItemsSearchFilters } from '~hooks/useItemsSearchFilters';
import { searchItemsV2 } from '~services/item';
import {
  ItemFilterV2Base,
  ItemExtended,
  ItemsSearchHead,
} from '~services/item/types';
import { ORDERS_FINALIZED_STATE_OPTIONS } from '~constants/ordersStatesOptions';

import { useDashboardVariantV2GiverContext } from '../../../Context';
// eslint-disable-next-line max-len
import { useDashboardVariantV2GiverFinalizedFiltersContext } from '../../../FinalizedFiltersContext';

import {
  DashboardVariantV2GiverContentFinalizedTabContextValue,
  DashboardVariantV2GiverContentFinalizedTabDialogFeedbackData,
  DashboardVariantV2GiverContentFinalizedTabDialogDetailsOrderData,
} from './types';
import {
  DEFAULT_PAGINATION,
  DEFAULT_FILTERS_PAYLOAD,
  DEFAULT_ROUTE_ITEM_STATES,
  DEFAULT_GIVER_COLLECT_ITEM_STATES,
} from './utils';

const [, , WITHOUT_VISITING_STATE_OPTION] = ORDERS_FINALIZED_STATE_OPTIONS;

const DashboardVariantV2GiverContentFinalizedTabContext =
  createContext<DashboardVariantV2GiverContentFinalizedTabContextValue>({
    parseFiltersRequest: () => DEFAULT_FILTERS_PAYLOAD,
    table: {
      data: [],
      stats: null,
      loading: true,
      error: null,
      reloadRequest: () => {},
      totalSize: 0,
      queryParams: {
        pagination: DEFAULT_PAGINATION,
        filters: DEFAULT_FILTERS_PAYLOAD,
      },
      handleInitialData: () => {},
      handleChangeTable: () => {},
      handleInitialTable: () => {},
    },
    actions: {
      reloadDataAndResetSelectedRows: () => {},
      handleForceRequests: () => {},
    },
    dialogs: {
      feedback: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      detailsOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
    },
  });

export const useDashboardVariantV2GiverContentFinalizedTabContext =
  (): DashboardVariantV2GiverContentFinalizedTabContextValue =>
    useContext(DashboardVariantV2GiverContentFinalizedTabContext);

const DashboardVariantV2GiverContentFinalizedTabProvider: FC = ({
  children,
}) => {
  const {
    activeTab: { currentTab },
    statistics: { reloadData: reloadStatistics },
  } = useDashboardVariantV2GiverContext();
  const { filters, forceRequest } =
    useDashboardVariantV2GiverFinalizedFiltersContext();

  const getParsedValuesItemStates = useCallback(
    (itemStates: Nullable<SelectOption[]>): Nullable<SelectOption[]> => {
      // The value is transformed to send all values ​​even if the selector does not contain them.
      if (itemStates !== null && itemStates.length > 0) {
        return itemStates.flatMap((state) => {
          if (state.value === RouteItemStateType.FinalizedError) {
            return [state, WITHOUT_VISITING_STATE_OPTION];
          }

          return state;
        });
      }

      return null;
    },
    [],
  );

  const parseFilters = useMemo<ItemsSearchFilters>(() => {
    const parsedValuesItemStates = getParsedValuesItemStates(
      filters.filters.itemState,
    );

    return { ...filters.filters, itemState: parsedValuesItemStates };
  }, [filters.filters, getParsedValuesItemStates]);

  const parseFiltersRequest = useParseFiltersRequestSearchItems({
    filters: parseFilters,
    activeFilterTabId: currentTab,
    defaultRouteItemState: DEFAULT_ROUTE_ITEM_STATES,
    defaultGiverCollectItemStateTypesId: DEFAULT_GIVER_COLLECT_ITEM_STATES,
  });

  const table = useDataTableRequest<
    PaginationRequestParams<ItemFilterV2Base>,
    DataPaginateResponse<ItemExtended[], null, ItemsSearchHead>,
    ErrorResponse
  >({
    request: searchItemsV2,
    payload: {
      pagination: DEFAULT_PAGINATION,
      filters: parseFiltersRequest(),
    },
  });

  const handleForceRequests = useCallback(
    (currentFilters?: Partial<ItemsSearchFilters>) => {
      table.handleChangeTable('filter', {
        pagination: {
          ...table.queryParams.pagination,
          page: 1,
        },
        filters: parseFiltersRequest(currentFilters),
      });

      forceRequest.toggleValue(false);
    },
    [forceRequest, parseFiltersRequest, table],
  );

  useEffect(() => {
    if (forceRequest.value) {
      handleForceRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceRequest.value]);

  const reloadDataAndResetSelectedRows = useCallback(() => {
    table.reloadRequest({
      pagination: table.queryParams.pagination,
      filters: parseFiltersRequest(),
    });

    reloadStatistics();
  }, [reloadStatistics, table, parseFiltersRequest]);

  const [
    openDialogFeedback,
    selectedDataForFeedback,
    handleOpenDialogFeedback,
    handleCloseDialogFeedback,
  ] =
    useToggleDialogConfirmation<
      Nullable<DashboardVariantV2GiverContentFinalizedTabDialogFeedbackData>
    >(null);

  const [
    openDialogDetailsOrder,
    selectedForDetailsOrder,
    handleOpenDialogDetailsOrder,
    handleCloseDialogDetailsOrder,
  ] =
    useToggleDialogConfirmation<
      Nullable<DashboardVariantV2GiverContentFinalizedTabDialogDetailsOrderData>
    >(null);

  return (
    <DashboardVariantV2GiverContentFinalizedTabContext.Provider
      value={{
        parseFiltersRequest,
        table,
        actions: {
          reloadDataAndResetSelectedRows,
          handleForceRequests,
        },
        dialogs: {
          feedback: {
            open: openDialogFeedback,
            data: selectedDataForFeedback,
            handleOpen: handleOpenDialogFeedback,
            handleClose: handleCloseDialogFeedback,
          },
          detailsOrder: {
            open: openDialogDetailsOrder,
            data: selectedForDetailsOrder,
            handleOpen: handleOpenDialogDetailsOrder,
            handleClose: handleCloseDialogDetailsOrder,
          },
        },
      }}
    >
      {children}
    </DashboardVariantV2GiverContentFinalizedTabContext.Provider>
  );
};

export default DashboardVariantV2GiverContentFinalizedTabProvider;
